import { call, put, select } from 'redux-saga/effects';
import * as actions from './actions';
import axios from 'axios';
import moment from 'moment';
import { waitForGetAdvancedSearchPaginatedCampaigns } from 'store/campaign/sagas';
import { getEnvVar } from 'helpers';
import { waitForGetSupportedEventTypes } from 'store/event/sagas';

const getCommunicationApiRoot = (tenantId, businessContextId) =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/communication-service/messagehistory/tenants/${tenantId}/context/${businessContextId}`;

const getCommunicationWithoutContextApiRoot = (tenantId) =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/communication-service/messagehistory/tenants/${tenantId}`;

const getGcmApiRoot = (tenantId, businessContextId) =>
  `${(window._env_ && window._env_.REACT_APP_USER_API_ROOT) ||
    process.env.REACT_APP_USER_API_ROOT ||
    ''}/gcm-service/tenants/${tenantId}/context/${businessContextId}`;

const getUtcToDate = (queryData) => {
  const selectedToDate = moment.utc(queryData?.toDate);
  const nowUtc = moment.utc();
  const isTodayInUtc = selectedToDate.isValid() && selectedToDate.isSame(nowUtc, 'day');

  if (queryData?.providerMetric && queryData?.dateType === 'DAY' && isTodayInUtc) {
    return nowUtc.toISOString();
  }

  return moment(queryData.toDate).endOf('day').toISOString();
};

const makeQueryData = (queryData, allActiveCampaigns) => {
  //For not campaign specific metric set all campaigns
  if (!queryData.campaignId) {
    let allActiveCampaignIds = allActiveCampaigns
      .map(campaign => campaign.id || campaign.uuid);
    queryData = { ...queryData, activeCampaignIds: allActiveCampaignIds };
  } else {
    let campaign = allActiveCampaigns.find(c => c.id === queryData?.campaignId || c.uuid === queryData?.campaignId);
    let campaignName = campaign ? campaign.name : '';
    queryData.activeCampaignIds && delete queryData.activeCampaignIds;
    queryData = { ...queryData, campaignName };
  }

  queryData.fromDate  = queryData.dateType === 'MONTH'
    ? moment(queryData.fromDate).format('YYYY-MM')
    : moment(queryData.fromDate).startOf('day').toISOString();
  queryData.toDate  = queryData.dateType === 'MONTH'
    ? moment(queryData.toDate).format('YYYY-MM')
    : getUtcToDate(queryData);
  return queryData;
};

const normalizeProviderMetric = metric => (metric === 'BOUNCED' ? 'BOUNCE' : metric);

export const getProviderMetrics = async queryData => {
  const { tenantId, businessContextId } = queryData;
  const payload = { ...queryData, providerMetric: normalizeProviderMetric(queryData.providerMetric) };

  return axios
    .post(
      `${getCommunicationApiRoot(tenantId, businessContextId)}/aggregated-provider-metrics`,
      payload
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export const getSearchMetrics = async queryData => {
  const {
    tenantId,
    businessContextId,
    type,
    campaignId,
    providerMetric,
    fromDate,
    toDate,
    index,
    pageSize
  } = queryData;
let params = {
    type,
    campaignId,
    providerMetric: normalizeProviderMetric(providerMetric),
    fromDate,
    toDate,
    index,
    pageSize
  };

  return axios
    .get(`${getCommunicationApiRoot(tenantId, businessContextId)}/search`, {
      params
    })
    .then(response => response)
    .catch(err => {
      throw err;
    });
};

export const getMessageHistory = async queryData => {
  const {
    tenantId,
    businessContextId,
    bodyParams
  } = queryData;

  return axios
    .post(`${getCommunicationWithoutContextApiRoot(tenantId)}/searchByMessageIds`,
      bodyParams
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export const getSearchMessageHistory = async queryData => {
  const {
    tenantId,
    businessContextId,
    type,
    campaignId,
    providerMetric,
    fromDate,
    toDate,
    index,
    pageSize
  } = queryData;
  let params = { type, campaignId, providerMetric: normalizeProviderMetric(providerMetric), fromDate, toDate };

  return axios
    .post(
      `${getGcmApiRoot(
        tenantId,
        businessContextId
      )}/event-report/search-message-history?index=${index}&pageSize=${pageSize}`,
      params
    )
    .then(response => response)
    .catch(err => {
      throw err;
    });
};

export const getCampaignReportCsvFileAsync = (
  tenantId, 
  businessContextId,
  payload
) => {
  const {
    type,
    campaignId,
    providerMetric,
    fromDate,
    toDate,
    index,
    pageSize
  } = payload;
  let params = { type, campaignId, providerMetric: normalizeProviderMetric(providerMetric), fromDate, toDate };
  return axios({
    url: `${getGcmApiRoot(
      tenantId,
      businessContextId
    )}/event-report/search-message-history/export?index=${index}&pageSize=${pageSize}`,
    method: 'POST',
    responseType: 'blob',
    data: params
  })
  .then(response => response.data)
    .catch(err => {
      throw err;
    });
}

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const selectedRowsPerPage = store => store.user.selectedRowsPerPage;
const campaignReportDataCount = store => store.reports.campaignReportDataCount;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const queryParamSelector = store => store.reports.queryData;
const productNamesSelector = store => store.events.supportedEventTypes.productNames;

export function* getContextReportDateIfNeeded(action) {
  yield call(waitForGetSupportedEventTypes);
  let campaignUuid = action?.payload?.campaignUuid;
  let queryData;
  queryData = action?.payload?.queryData
    ? { ...action?.payload.queryData }
    : { ...(yield select(queryParamSelector)) };
  queryData.tenantId = yield select(currentTenantId);
  queryData.businessContextId = yield select(currentBusinessContextId);
  let productNames = yield select(productNamesSelector);

  try {
    let allCampaigns = yield call(waitForGetAdvancedSearchPaginatedCampaigns);
    let allActiveCampaigns = allCampaigns.filter(campaign => productNames.includes(campaign.productName));
    queryData = allActiveCampaigns && makeQueryData(queryData, allActiveCampaigns);
    if (
      (allActiveCampaigns?.length > 0 &&
        queryData.activeCampaignIds &&
        queryData['activeCampaignIds']?.length > 0) ||
      queryData?.campaignId
    ) {
      yield put(actions.gettingContextReportData({ queryData }));
      if ((campaignUuid && queryData?.campaignId) || (!campaignUuid && queryData?.activeCampaignIds)) {
        let contextReportData = yield call(getProviderMetrics, queryData);
        yield put(
          actions.gettingContextReportDataSucceeded({
            queryData,
            contextReportData
          })
        );
      }
    } else {
      yield put(
        actions.gettingContextReportDataFailed({
          error: {
            message:
              'No campaigns are created or No active campaigns are available for the context'
          }
        })
      );
    }
  } catch (ex) {
    yield put(
      actions.gettingContextReportDataFailed({ error: ex?.response?.data })
    );
  }
}

export function* getCampaignReportDateIfNeeded(action) {
  yield call(waitForGetSupportedEventTypes);
  let queryData = { ...action.payload.queryData };
  const suppressUserMessage = action?.payload?.suppressUserMessage;
  queryData.tenantId = yield select(currentTenantId);
  queryData.businessContextId = yield select(currentBusinessContextId);
  queryData.pageSize = yield select(selectedRowsPerPage);
  let productNames = yield select(productNamesSelector);

  try {
    let allCampaigns = yield call(waitForGetAdvancedSearchPaginatedCampaigns);
    let allActiveCampaigns = allCampaigns.filter(campaign => productNames.includes(campaign.productName));
    queryData = allActiveCampaigns && makeQueryData(queryData, allActiveCampaigns);
    yield put(actions.gettingCampaignReportData({ queryData }));
    if (queryData?.campaignId) {
      let campaignReportData = yield call(getSearchMessageHistory, {
        ...queryData
      });
      yield put(
        actions.gettingCampaignReportDataSucceeded({
          queryData,
          campaignReportData
        })
      );
    }
  } catch (ex) {
    yield put(
      actions.gettingCampaignReportDataFailed({
        error: ex?.response?.data,
        suppressUserMessage
      })
    );
  }
}

export function* getCampaignReportCsvFileIfNeeded(action) {
  let tenantId = yield select(currentTenantId);
  let businessContextId = yield select(currentBusinessContextId);
  let totalCount = yield select(campaignReportDataCount)
  let payload = { ...action.payload };
  payload.index = 0;
  payload.pageSize = Number(getEnvVar('ADM_CAMPAIGN_REPORT_EXPORT_COUNT')) || totalCount;
  payload.fromDate = moment(payload.fromDate)
    .startOf('day')
    .toISOString();
  payload.toDate = moment(payload.toDate)
    .endOf('day')
    .toISOString();
  payload.campaignId = payload.campaignId;
  try {
    let response = yield call(getCampaignReportCsvFileAsync, tenantId, businessContextId, payload );
    yield put(actions.gettingCampaignReportCsvFileSucceeded(response));
  } catch (ex) {
    yield put(actions.gettingCampaignReportCsvFileFailed(ex));
  }
}

export function* getMessageHistoryIfNeeded(action) {
  let tenantId = yield select(currentTenantId);
  let businessContextId = yield select(currentBusinessContextId);
  let { messageUuid } = action.payload;
  let bodyParams = [messageUuid];
  try {
    let messageHistory = yield call(getMessageHistory, {
      tenantId,
      businessContextId,
      bodyParams
    });
    yield put(actions.gettingMessageHistorySucceeded({ data: messageHistory }));
  } catch (ex) {
    yield put(actions.gettingMessageHistoryFailed({ error: ex }));
  }
}

export const getEmailClickMetricsAsync = async queryData => {
  const {
    tenantId,
    businessContextId,
    campaignId,
    fromDate,
    toDate
  } = queryData;

  const params = { fromDate, toDate };

  return axios
    .get(
      `${getCommunicationApiRoot(tenantId, businessContextId)}/campaignId/${campaignId}`,
      { params }
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export function* getEmailClickMetricsIfNeeded(action) {
  let tenantId = yield select(currentTenantId);
  let businessContextId = yield select(currentBusinessContextId);
  const { campaignId, fromDate, toDate } = action.payload;

  const fromDateUtc = moment
    .utc(moment(fromDate).startOf('day').toISOString())
    .format('YYYY-MM-DD');
  const toDateUtc = moment
    .utc(moment(toDate).endOf('day').toISOString())
    .format('YYYY-MM-DD');

  const payload = {
    tenantId,
    businessContextId,
    campaignId,
    fromDate: fromDateUtc,
    toDate: toDateUtc
  };

  try {
    yield put(actions.gettingEmailClickMetrics());
    const data = yield call(getEmailClickMetricsAsync, payload);
    yield put(actions.gettingEmailClickMetricsSucceeded({ data }));
  } catch (ex) {
    yield put(actions.gettingEmailClickMetricsFailed({ error: ex?.response?.data || ex }));
  }
}

export const getClickedUrlsAsync = async (tenantId, contextId, messageId) => {
  return axios
    .get(
      `${getCommunicationApiRoot(tenantId, contextId)}/messageId/${messageId}`,
      {
        headers: {
          'accept': 'application/json, text/plain, */*',
          'content-type': 'application/json'
        }
      }
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export function* getClickedUrlsIfNeeded(action) {
  let tenantId = yield select(currentTenantId);
  let businessContextId = yield select(currentBusinessContextId);
  const { messageId } = action.payload;

  if (!messageId) {
    yield put(actions.gettingClickedUrlsFailed({ error: { message: 'No messageId provided' } }));
    return;
  }

  try {
    const data = yield call(getClickedUrlsAsync, tenantId, businessContextId, messageId);
    yield put(actions.gettingClickedUrlsSucceeded({ data }));
  } catch (ex) {
    yield put(actions.gettingClickedUrlsFailed({ error: ex?.response?.data || ex }));
  }
}